import {EmailDeliveryStatus, UserRole} from "../../GraphQL/generated/graphql";

export type OrganizationMemberUser = {
    id: string;
    email?: string | null;
    userName?: string | null;
    firstName?: string | null;
    lastName?: string | null;
    phoneNumber?: string | null;
    emailConfirmed: boolean;
    isActive: boolean;
    isDeleted: boolean;
    defaultRole: UserRole;
    onboardingEmailStatus?: EmailDeliveryStatus | null;
    onboardingEmailQueuedAtUtc?: string | null;
    onboardingEmailLastSentAtUtc?: string | null;
    onboardingEmailLastFailedAtUtc?: string | null;
    onboardingEmailSentCount?: number | null;
    onboardingEmailFailureReason?: string | null;
};

export type OrganizationMemberCreateFormValues = {
    email: string;
    firstName?: string;
    lastName?: string;
    phoneNumber?: string;
};

export type OrganizationMemberUpdateFormValues = {
    firstName?: string;
    lastName?: string;
    phoneNumber?: string;
    defaultRole: UserRole;
    isActive: boolean;
};

export type OrganizationMemberUpdateInput = {
    userId: string;
    firstName?: string;
    lastName?: string;
    phoneNumber?: string;
    role: UserRole;
    isActive: boolean;
};

export const isOrganizationMemberAccount = (user?: OrganizationMemberUser | null): user is OrganizationMemberUser =>
    !!user &&
    !user.isDeleted &&
    (user.defaultRole === UserRole.Administrator || user.defaultRole === UserRole.User);

export const isPendingOrganizationMember = (user: OrganizationMemberUser): boolean =>
    user.defaultRole === UserRole.User && user.isActive && !user.isDeleted && !user.emailConfirmed;

export const isOrganizationMemberOnboardingQueued = (user: OrganizationMemberUser): boolean =>
    user.onboardingEmailStatus === EmailDeliveryStatus.Queued;

export const getOrganizationMemberDisplayName = (user: Pick<OrganizationMemberUser, "email" | "firstName" | "lastName" | "userName">): string => {
    const fullName = [user.firstName, user.lastName]
        .map(value => value?.trim())
        .filter(Boolean)
        .join(" ");

    return fullName || user.email || user.userName || "-";
};

export const buildOrganizationMemberCreateInput = (values: OrganizationMemberCreateFormValues): OrganizationMemberCreateFormValues => ({
    email: values.email.trim(),
    firstName: values.firstName?.trim() || undefined,
    lastName: values.lastName?.trim() || undefined,
    phoneNumber: values.phoneNumber?.trim() || undefined
});

export const buildOrganizationMemberUpdateInput = (
    user: Pick<OrganizationMemberUser, "id">,
    values: OrganizationMemberUpdateFormValues
): OrganizationMemberUpdateInput => ({
    userId: user.id,
    firstName: values.firstName?.trim() || undefined,
    lastName: values.lastName?.trim() || undefined,
    phoneNumber: values.phoneNumber?.trim() || undefined,
    role: values.defaultRole,
    isActive: values.isActive
});

export const buildOrganizationMemberStatusToggleInput = (
    user: OrganizationMemberUser,
    isActive: boolean
): OrganizationMemberUpdateInput => buildOrganizationMemberUpdateInput(user, {
    firstName: user.firstName ?? undefined,
    lastName: user.lastName ?? undefined,
    phoneNumber: user.phoneNumber ?? undefined,
    defaultRole: user.defaultRole,
    isActive
});
